/**
 * FirebaseUI - Cassette Future styled UI components for Firebase cloud services
 *
 * Provides:
 * - AccountPanel: Account status, sign-in/sign-up, sync status
 * - LeaderboardModal: All-time, weekly, daily, friends leaderboards
 * - FriendsModal: Friend list, add friend, pending requests
 */

// Polyfill for crypto.randomUUID (missing in iOS Firefox's WebKit)
// Firefox's autofill script crashes without this
if (typeof crypto !== 'undefined' && !crypto.randomUUID) {
    crypto.randomUUID = function() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            const r = Math.random() * 16 | 0;
            const v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    };
}

/**
 * Create an element with optional classes and text
 * @param {string} tag - HTML tag name
 * @param {string|string[]} [className] - CSS class(es)
 * @param {string} [text] - Text content
 * @returns {HTMLElement}
 */
function createElement(tag, className, text) {
    const el = document.createElement(tag);
    if (className) {
        if (Array.isArray(className)) {
            el.classList.add(...className);
        } else {
            el.className = className;
        }
    }
    if (text !== undefined) {
        el.textContent = text;
    }
    return el;
}

/**
 * Clear all children from an element
 * @param {HTMLElement} el - Element to clear
 */
function clearChildren(el) {
    while (el.firstChild) el.removeChild(el.firstChild);
}

/**
 * Create a cassette-future styled modal
 * @param {object} options
 * @param {string} options.title - Modal title
 * @param {string} [options.className] - Additional class for modal
 * @param {function} [options.onClose] - Close callback
 * @returns {{overlay: HTMLElement, modal: HTMLElement, body: HTMLElement, close: function}}
 */
function createCFModal({ title, className, onClose }) {
    const overlay = document.createElement('div');
    overlay.className = 'cf-ui cf-modal-overlay';

    const modal = document.createElement('div');
    modal.className = 'cf-modal' + (className ? ` ${className}` : '');

    // Header
    const header = document.createElement('div');
    header.className = 'cf-modal-header';

    const titleEl = document.createElement('h2');
    titleEl.className = 'cf-modal-title';
    titleEl.textContent = title;
    header.appendChild(titleEl);

    const closeBtn = document.createElement('button');
    closeBtn.className = 'cf-modal-close';
    closeBtn.textContent = '×';  // Unicode multiplication sign (U+00D7)
    header.appendChild(closeBtn);

    modal.appendChild(header);

    // Body
    const body = document.createElement('div');
    body.className = 'cf-modal-body';
    modal.appendChild(body);

    overlay.appendChild(modal);
    document.body.appendChild(overlay);

    const close = () => {
        if (onClose) onClose();
        overlay.remove();
    };

    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', (e) => {
        if (e.target === overlay) close();
    });

    // Escape key
    const handleEscape = (e) => {
        if (e.key === 'Escape') {
            close();
            document.removeEventListener('keydown', handleEscape);
        }
    };
    document.addEventListener('keydown', handleEscape);

    return { overlay, modal, body, close };
}

/**
 * Shows a CF-styled modal asking if user wants to migrate anonymous progress
 * @returns {Promise<boolean>} true if user wants to migrate
 */
function showMigrationConfirmation() {
    return new Promise((resolve) => {
        let resolved = false;

        const modal = createCFModal({
            title: 'TRANSFER PROGRESS?',
            onClose: () => {
                if (!resolved) {
                    resolved = true;
                    resolve(false);
                }
            }
        });
        const content = modal.body;

        // Icon and message
        const messageSection = createElement('div', ['cf-text-center', 'cf-mb-md']);
        const iconDiv = createElement('div', null, '\u{1F697}'); // 🚗
        iconDiv.style.cssText = 'font-size: 40px; margin-bottom: var(--cf-space-md); opacity: 0.7;';
        messageSection.appendChild(iconDiv);
        const p1 = createElement('p', null, 'You have existing progress as an anonymous driver.');
        p1.style.cssText = 'margin: 0; font-size: var(--cf-font-size-sm); color: var(--cf-text-secondary);';
        messageSection.appendChild(p1);
        const p2 = createElement('p', null, 'Would you like to transfer it to your new account?');
        p2.style.cssText = 'margin: var(--cf-space-sm) 0 0 0; font-size: var(--cf-font-size-sm); color: var(--cf-text-secondary);';
        messageSection.appendChild(p2);
        content.appendChild(messageSection);

        // Buttons
        const buttonGroup = document.createElement('div');
        buttonGroup.className = 'cf-button-group';

        const startFreshBtn = document.createElement('button');
        startFreshBtn.className = 'cf-button';
        startFreshBtn.textContent = 'START FRESH';

        const transferBtn = document.createElement('button');
        transferBtn.className = 'cf-button primary';
        transferBtn.textContent = 'TRANSFER';

        buttonGroup.appendChild(startFreshBtn);
        buttonGroup.appendChild(transferBtn);
        content.appendChild(buttonGroup);

        startFreshBtn.addEventListener('click', () => {
            resolved = true;
            resolve(false);
            modal.close();
        });

        transferBtn.addEventListener('click', () => {
            resolved = true;
            resolve(true);
            modal.close();
        });
    });
}

/**
 * Show Account Panel modal
 * @param {object} options
 * @param {AccountManager} options.accountManager
 * @param {CloudSaveManager} options.cloudSaveManager
 * @param {ModalManager} options.modalManager
 * @param {SaveManager} [options.saveManager] - For migration modal
 * @param {string} [options.context='menu'] - 'menu' or 'game'
 */
export function showAccountPanel({ accountManager, cloudSaveManager, modalManager, saveManager, context = 'menu' }) {
    const modal = createCFModal({ title: 'ACCOUNT' });
    const content = modal.body;

    const isLinked = accountManager.isLinked();
    const email = accountManager.getEmail();
    const friendCode = accountManager.getFriendCode();

    // Account status section
    if (isLinked) {
        // Signed in display
        const signedInSection = createElement('div', 'cf-section');
        const dataRow = createElement('div', 'cf-data-row');
        dataRow.appendChild(createElement('span', 'cf-data-label', 'Signed in as'));
        dataRow.appendChild(createElement('span', 'cf-data-value', email));
        signedInSection.appendChild(dataRow);
        content.appendChild(signedInSection);
    } else {
        // Anonymous warning
        const alertSection = createElement('div', 'cf-alert');
        alertSection.appendChild(createElement('div', 'cf-alert-text', 'Anonymous Account'));
        alertSection.appendChild(createElement('div', 'cf-alert-subtext', 'Link with email to secure your progress and enable leaderboards.'));
        content.appendChild(alertSection);
    }

    // Friend code display
    if (friendCode) {
        const codeSection = createElement('div', 'cf-info-block');
        codeSection.appendChild(createElement('div', ['cf-data-label', 'cf-mb-sm'], 'Your Friend Code'));
        const flexDiv = createElement('div', ['cf-flex', 'cf-items-center', 'cf-gap-md']);
        flexDiv.appendChild(createElement('span', 'cf-code-display', friendCode));
        const copyBtn = createElement('button', ['cf-button', 'cf-button-small'], 'COPY');
        copyBtn.id = 'cf-copy-code';
        flexDiv.appendChild(copyBtn);
        codeSection.appendChild(flexDiv);
        content.appendChild(codeSection);

        copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(friendCode);
            copyBtn.textContent = 'COPIED';
            setTimeout(() => copyBtn.textContent = 'COPY', 1500);
        });
    }

    // Auth form section (only if not linked)
    if (!isLinked) {
        const formSection = document.createElement('div');
        formSection.className = 'cf-section';

        // Tabs
        const tabs = createElement('div', 'cf-tabs');
        const signupTab = createElement('button', ['cf-tab', 'active'], 'Create Account');
        signupTab.dataset.mode = 'signup';
        const signinTab = createElement('button', 'cf-tab', 'Sign In');
        signinTab.dataset.mode = 'signin';
        tabs.appendChild(signupTab);
        tabs.appendChild(signinTab);
        formSection.appendChild(tabs);

        // Form - use proper <form> element for iOS compatibility
        const form = document.createElement('form');
        form.setAttribute('action', 'javascript:void(0)');

        // Display name row
        const nameRow = createElement('div', 'cf-mb-sm');
        nameRow.id = 'cf-name-row';
        const displayNameDiv = createElement('div', 'cf-input');
        displayNameDiv.id = 'cf-displayname';
        displayNameDiv.contentEditable = 'true';
        displayNameDiv.dataset.placeholder = 'Display name';
        displayNameDiv.style.minHeight = '1.5em';
        nameRow.appendChild(displayNameDiv);
        form.appendChild(nameRow);

        // Email row
        const emailRow = createElement('div', 'cf-mb-sm');
        const emailInput = createElement('input', 'cf-input');
        emailInput.type = 'email';
        emailInput.id = 'cf-email';
        emailInput.placeholder = 'Email address';
        emailInput.autocomplete = 'off';
        emailInput.dataset.lpignore = 'true';
        emailInput.dataset.formType = 'other';
        emailRow.appendChild(emailInput);
        form.appendChild(emailRow);

        // Password row
        const passRow = createElement('div', 'cf-mb-sm');
        const passInput = createElement('input', 'cf-input');
        passInput.type = 'password';
        passInput.id = 'cf-password';
        passInput.placeholder = 'Password';
        passRow.appendChild(passInput);
        form.appendChild(passRow);

        // Confirm password row
        const confirmRow = createElement('div', 'cf-mb-sm');
        confirmRow.id = 'cf-confirm-row';
        const confirmInput = createElement('input', 'cf-input');
        confirmInput.type = 'password';
        confirmInput.id = 'cf-confirm';
        confirmInput.placeholder = 'Confirm password';
        confirmRow.appendChild(confirmInput);
        form.appendChild(confirmRow);

        // Error and success messages
        const errorDiv = createElement('div', 'cf-error-text');
        errorDiv.id = 'cf-form-error';
        errorDiv.style.display = 'none';
        form.appendChild(errorDiv);

        const successDiv = createElement('div', 'cf-success-text');
        successDiv.id = 'cf-form-success';
        successDiv.style.display = 'none';
        form.appendChild(successDiv);

        // Submit button
        const submitBtn = createElement('button', ['cf-button', 'cf-button-full', 'primary', 'cf-mt-md'], 'CREATE ACCOUNT');
        submitBtn.type = 'button';
        submitBtn.id = 'cf-submit';
        form.appendChild(submitBtn);

        // Forgot password button
        const forgotBtn = createElement('button', ['cf-button', 'cf-button-full', 'cf-mt-sm'], 'FORGOT PASSWORD');
        forgotBtn.type = 'button';
        forgotBtn.id = 'cf-forgot';
        forgotBtn.style.display = 'none';
        form.appendChild(forgotBtn);

        formSection.appendChild(form);

        // Contenteditable placeholder and maxlength handling
        if (displayNameDiv.contentEditable === 'true') {
            // Show placeholder when empty
            const updatePlaceholder = () => {
                if (!displayNameDiv.textContent.trim()) {
                    displayNameDiv.setAttribute('data-empty', 'true');
                } else {
                    displayNameDiv.removeAttribute('data-empty');
                }
            };
            displayNameDiv.addEventListener('input', () => {
                // Enforce maxlength
                if (displayNameDiv.textContent.length > 20) {
                    displayNameDiv.textContent = displayNameDiv.textContent.slice(0, 20);
                    // Move cursor to end
                    const range = document.createRange();
                    range.selectNodeContents(displayNameDiv);
                    range.collapse(false);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                }
                updatePlaceholder();
            });
            displayNameDiv.addEventListener('blur', updatePlaceholder);
            displayNameDiv.addEventListener('focus', updatePlaceholder);
            updatePlaceholder();
            // Add placeholder style
            const style = document.createElement('style');
            style.textContent = `
                #cf-displayname[data-empty="true"]:before {
                    content: attr(data-placeholder);
                    color: var(--cf-text-disabled, #666);
                    pointer-events: none;
                }
            `;
            document.head.appendChild(style);
        }


        let isSignUp = true;

        const setMode = (mode) => {
            isSignUp = mode === 'signup';
            nameRow.style.display = isSignUp ? 'block' : 'none';
            confirmRow.style.display = isSignUp ? 'block' : 'none';
            forgotBtn.style.display = isSignUp ? 'none' : 'block';
            submitBtn.textContent = isSignUp ? 'CREATE ACCOUNT' : 'SIGN IN';
            errorDiv.style.display = 'none';
            successDiv.style.display = 'none';

            tabs.querySelectorAll('.cf-tab').forEach(tab => {
                tab.classList.toggle('active', tab.dataset.mode === mode);
            });
        };

        tabs.addEventListener('click', (e) => {
            if (e.target.classList.contains('cf-tab')) {
                setMode(e.target.dataset.mode);
            }
        });

        submitBtn.addEventListener('click', async () => {
            const displayName = (displayNameDiv.value || displayNameDiv.textContent || '').trim();
            const email = emailInput.value.trim();
            const password = passInput.value;
            const confirm = confirmInput.value;

            errorDiv.style.display = 'none';
            successDiv.style.display = 'none';

            if (isSignUp && !displayName) {
                errorDiv.textContent = 'Please enter a display name';
                errorDiv.style.display = 'block';
                return;
            }

            if (isSignUp && displayName.length > 20) {
                errorDiv.textContent = 'Display name must be 20 characters or less';
                errorDiv.style.display = 'block';
                return;
            }

            if (!email || !password) {
                errorDiv.textContent = 'Please enter email and password';
                errorDiv.style.display = 'block';
                return;
            }

            if (isSignUp && password !== confirm) {
                errorDiv.textContent = 'Passwords do not match';
                errorDiv.style.display = 'block';
                return;
            }

            if (password.length < 6) {
                errorDiv.textContent = 'Password must be at least 6 characters';
                errorDiv.style.display = 'block';
                return;
            }

            submitBtn.disabled = true;
            submitBtn.textContent = isSignUp ? 'CREATING...' : 'SIGNING IN...';

            try {
                if (isSignUp) {
                    // Check all possible save locations for anonymous data
                    const legacySave = localStorage.getItem('roadtripGameState');
                    const anonymousSave = localStorage.getItem('roadTripSave_anonymous');

                    // Use whichever exists (prefer anonymous slot, fall back to legacy)
                    const anonymousSaveData = anonymousSave || legacySave;

                    // CRITICAL: Write to account slot BEFORE linkWithEmail
                    // This prevents auth handlers from syncing empty state to cloud
                    if (anonymousSaveData) {
                        localStorage.setItem('roadTripSave_account', anonymousSaveData);
                    }

                    await accountManager.ensureSignedIn();
                    await accountManager.linkWithEmail(email, password);
                    await accountManager.updateDisplayName(displayName);

                    // Check for anonymous progress to migrate
                    if (anonymousSaveData) {
                        const shouldMigrate = await showMigrationConfirmation();

                        if (shouldMigrate) {
                            // Update with owner ID and sync to cloud
                            const state = JSON.parse(anonymousSaveData);
                            state._cloudSaveOwnerId = accountManager.currentUser?.uid;

                            localStorage.setItem('roadTripSave_account', JSON.stringify(state));
                            localStorage.removeItem('roadTripSave_anonymous');
                            localStorage.removeItem('roadtripGameState');

                            // Force sync to cloud immediately
                            if (cloudSaveManager) {
                                await cloudSaveManager.forceLocalToCloud(state);
                            }
                        } else {
                            // User chose START FRESH - clear all existing saves and set flag
                            // The auth handler will create a fresh initial state on reload
                            localStorage.removeItem('roadTripSave_account');
                            localStorage.removeItem('roadTripSave_anonymous');
                            localStorage.removeItem('roadtripGameState');
                            // Also clear simulator states to prevent old progress from being resumed
                            localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
                            localStorage.removeItem('offlineSimulatorState_roadTripSave_anonymous');
                            localStorage.setItem('idlemile_start_fresh', 'true');
                        }
                    } else {
                        // No anonymous data - still set flag for fresh start
                        // Clear any lingering simulator states
                        localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
                        localStorage.setItem('idlemile_start_fresh', 'true');
                    }

                    successDiv.textContent = 'Account created!';
                    successDiv.style.display = 'block';

                    // Reload to apply signed-in state
                    setTimeout(() => {
                        modal.close();
                        window.location.reload();
                    }, 1000);
                } else {
                    // Sign in flow
                    console.log('[FirebaseUI] Sign in flow starting...');

                    // IMPORTANT: Capture current game state BEFORE signing in
                    // The auth handler will overwrite gameState.state during signIn
                    const app = window.roadTripApp;
                    const localState = app?.gameState?.state ? JSON.parse(JSON.stringify(app.gameState.state)) : null;
                    console.log('[FirebaseUI] Captured local state before sign-in:', {
                        lifetimeMiles: localState?.lifetimeMiles,
                        totalMiles: localState?.totalMiles
                    });

                    // Tell auth handler to skip its sync - we'll handle it here
                    window._firebaseUIHandlingLogin = true;

                    await accountManager.signInWithEmail(email, password);
                    console.log('[FirebaseUI] Sign in completed');

                    const userId = accountManager.getUserId();

                    // Load cloud save
                    const cloudData = await cloudSaveManager.loadFromCloudWithMeta();
                    const cloudState = cloudData?.state;

                    console.log('[FirebaseUI] Checking saves:', {
                        hasLocal: !!localState,
                        hasCloud: !!cloudState,
                        localLifetimeMiles: localState?.lifetimeMiles,
                        localTotalMiles: localState?.totalMiles,
                        cloudLifetimeMiles: cloudState?.lifetimeMiles,
                        cloudTotalMiles: cloudState?.totalMiles,
                        cloudSavedAt: cloudData?.savedAt ? new Date(cloudData.savedAt).toISOString() : null
                    });

                    if (localState && cloudState) {
                        // Both exist - show conflict modal
                        console.log('[FirebaseUI] Both saves exist, showing conflict modal');
                        successDiv.textContent = 'Checking saves...';
                        successDiv.style.display = 'block';

                        const choice = await showSaveConflictChoice({
                            localState,
                            cloudState,
                            cloudTimestamp: cloudData.savedAt
                        });

                        console.log('[FirebaseUI] User chose:', choice);

                        if (choice === 'cancelled') {
                            // User X'd out - sign them back out and stay anonymous
                            console.log('[FirebaseUI] User cancelled - signing out');
                            window._firebaseUIHandlingLogin = false;
                            await accountManager.signOut();
                            modal.close();
                            return; // Don't reload, just close the modal
                        } else if (choice === 'cloud') {
                            // Use cloud save
                            cloudState._cloudSaveOwnerId = userId;
                            localStorage.setItem('roadTripSave_account', JSON.stringify(cloudState));
                            // Clear stale offline simulator state to prevent fuel consumption from old timestamps
                            localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
                        } else {
                            // Use local save - push to cloud
                            localState._cloudSaveOwnerId = userId;
                            localStorage.setItem('roadTripSave_account', JSON.stringify(localState));
                            await cloudSaveManager.forceLocalToCloud(localState);
                        }
                    } else if (cloudState && !localState) {
                        // Only cloud exists - use it
                        cloudState._cloudSaveOwnerId = userId;
                        localStorage.setItem('roadTripSave_account', JSON.stringify(cloudState));
                        // Clear stale offline simulator state to prevent fuel consumption from old timestamps
                        localStorage.removeItem('offlineSimulatorState_roadTripSave_account');
                    } else if (localState && !cloudState) {
                        // Only local exists - push to cloud
                        localState._cloudSaveOwnerId = userId;
                        await cloudSaveManager.forceLocalToCloud(localState);
                    }

                    // Clear the flag - we're done handling login
                    window._firebaseUIHandlingLogin = false;

                    successDiv.textContent = 'Signed in!';
                    successDiv.style.display = 'block';

                    // Reload to apply signed-in state
                    setTimeout(() => {
                        modal.close();
                        window.location.reload();
                    }, 500);
                }
            } catch (err) {
                window._firebaseUIHandlingLogin = false; // Clear flag on error
                // Handle both string errors (from _getReadableError) and Error objects
                const errorMessage = typeof err === 'string' ? err : (err?.message || 'Authentication failed');
                // Reset button state (but don't call setMode which would hide the error)
                submitBtn.disabled = false;
                submitBtn.textContent = isSignUp ? 'CREATE ACCOUNT' : 'SIGN IN';
                // Show error AFTER resetting button
                errorDiv.textContent = errorMessage;
                errorDiv.style.display = 'block';
            }
        });

        forgotBtn.addEventListener('click', async () => {
            const email = emailInput.value.trim();
            if (!email) {
                errorDiv.textContent = 'Please enter your email address';
                errorDiv.style.display = 'block';
                return;
            }

            try {
                await accountManager.sendPasswordReset(email);
                successDiv.textContent = 'Password reset email sent!';
                successDiv.style.display = 'block';
                errorDiv.style.display = 'none';
            } catch (err) {
                errorDiv.textContent = typeof err === 'string' ? err : (err.message || 'Failed to send reset email');
                errorDiv.style.display = 'block';
            }
        });

        content.appendChild(formSection);
    }

    // Sync status section
    if (cloudSaveManager && isLinked) {
        const syncSection = document.createElement('div');
        syncSection.className = 'cf-section';

        const updateSyncStatus = (status) => {
            const lastSync = cloudSaveManager.getLastSyncTime();
            const lastSyncText = lastSync ? new Date(lastSync).toLocaleTimeString() : 'Never';

            let dotClass = '';
            let statusText = 'Synced';

            if (status?.status === 'syncing') {
                dotClass = 'syncing';
                statusText = 'Syncing...';
            } else if (status?.status === 'error') {
                dotClass = 'error';
                statusText = 'Sync error';
            }

            clearChildren(syncSection);
            const syncStatusDiv = createElement('div', 'cf-sync-status');
            const dotSpan = createElement('span', dotClass ? ['cf-sync-dot', dotClass] : 'cf-sync-dot');
            const statusSpan = createElement('span', null, statusText);
            const lastSpan = createElement('span', null, `Last: ${lastSyncText}`);
            lastSpan.style.marginLeft = 'auto';
            syncStatusDiv.appendChild(dotSpan);
            syncStatusDiv.appendChild(statusSpan);
            syncStatusDiv.appendChild(lastSpan);
            syncSection.appendChild(syncStatusDiv);
        };

        updateSyncStatus();
        const unsubscribe = cloudSaveManager.onSyncStatusChanged(updateSyncStatus);
        modal.overlay.addEventListener('remove', unsubscribe);

        content.appendChild(syncSection);
    }

    // Manual Backup section (linked users only)
    if (cloudSaveManager && isLinked) {
        const backupSection = document.createElement('div');
        backupSection.className = 'cf-section';

        // Section title
        const backupTitle = document.createElement('div');
        backupTitle.className = 'cf-section-title';
        backupTitle.textContent = 'MANUAL BACKUP';
        backupTitle.style.cssText = 'margin-bottom: var(--cf-space-sm); font-size: var(--cf-font-size-xs); color: var(--cf-text-secondary); letter-spacing: 1px;';
        backupSection.appendChild(backupTitle);

        // Backup info display (will be updated async)
        const backupInfo = document.createElement('div');
        backupInfo.className = 'cf-backup-info';
        backupInfo.style.cssText = 'padding: var(--cf-space-sm); background: rgba(0,0,0,0.3); border-radius: 4px; margin-bottom: var(--cf-space-sm);';
        clearChildren(backupInfo);
        backupInfo.appendChild(createElement('span', 'cf-muted', 'Loading...'));
        backupSection.appendChild(backupInfo);

        // Button row
        const buttonRow = document.createElement('div');
        buttonRow.style.cssText = 'display: flex; gap: var(--cf-space-sm);';

        const saveBtn = document.createElement('button');
        saveBtn.className = 'cf-button';
        saveBtn.style.cssText = 'flex: 1;';
        saveBtn.textContent = 'SAVE TO BACKUP';
        saveBtn.disabled = true;

        const loadBtn = document.createElement('button');
        loadBtn.className = 'cf-button';
        loadBtn.style.cssText = 'flex: 1;';
        loadBtn.textContent = 'LOAD FROM BACKUP';
        loadBtn.disabled = true;

        buttonRow.appendChild(saveBtn);
        buttonRow.appendChild(loadBtn);
        backupSection.appendChild(buttonRow);

        // Error display
        const errorDisplay = document.createElement('div');
        errorDisplay.className = 'cf-error-text';
        errorDisplay.style.display = 'none';
        backupSection.appendChild(errorDisplay);

        content.appendChild(backupSection);

        // Format date helper
        const formatBackupDate = (timestamp) => {
            if (!timestamp) return 'Unknown';
            const date = new Date(timestamp);
            return date.toLocaleDateString(undefined, {
                month: 'short', day: 'numeric', year: 'numeric',
                hour: 'numeric', minute: '2-digit'
            });
        };

        // Get current game state
        const getCurrentState = () => {
            return window.roadTripApp?.gameState?.state;
        };

        // Load backup info and update UI
        let cachedBackupInfo = null;
        const refreshBackupInfo = async () => {
            try {
                cachedBackupInfo = await cloudSaveManager.getBackupInfo();
                if (cachedBackupInfo) {
                    const miles = Math.floor(cachedBackupInfo.lifetimeMiles).toLocaleString();
                    clearChildren(backupInfo);
                    const milesDiv = createElement('div');
                    milesDiv.style.cssText = 'font-size: var(--cf-font-size-sm); margin-bottom: 4px;';
                    const milesStrong = createElement('strong', null, miles);
                    milesDiv.appendChild(milesStrong);
                    milesDiv.appendChild(document.createTextNode(' lifetime miles'));
                    backupInfo.appendChild(milesDiv);
                    const savedDiv = createElement('div', 'cf-muted', `Saved: ${formatBackupDate(cachedBackupInfo.savedAt)}`);
                    savedDiv.style.fontSize = 'var(--cf-font-size-xs)';
                    backupInfo.appendChild(savedDiv);
                    loadBtn.disabled = false;
                } else {
                    clearChildren(backupInfo);
                    backupInfo.appendChild(createElement('span', 'cf-muted', 'No backup saved yet'));
                    loadBtn.disabled = true;
                }
                saveBtn.disabled = false;
            } catch (err) {
                clearChildren(backupInfo);
                backupInfo.appendChild(createElement('span', 'cf-muted', 'Failed to load backup info'));
                saveBtn.disabled = false;
                loadBtn.disabled = true;
            }
        };

        // Perform backup save
        const doSaveBackup = async () => {
            const state = getCurrentState();
            if (!state) {
                errorDisplay.textContent = 'No game state to save';
                errorDisplay.style.display = 'block';
                return;
            }

            console.log('[Backup] Saving state:', {
                miles: state.miles,
                totalMiles: state.totalMiles,
                lifetimeMiles: state.lifetimeMiles,
                fuel: state.fuel,
                momentum: state.session?.exitMomentum,
                prestigeLevel: state.prestige?.level,
                wavesNextMile: state.waves?.nextWaveAtMile,
                starterSpeed: state.cars?.starter?.upgradeLevels?.baseSpeed
            });

            saveBtn.disabled = true;
            saveBtn.textContent = 'SAVING...';
            errorDisplay.style.display = 'none';

            try {
                await cloudSaveManager.saveToBackup(state);
                saveBtn.textContent = 'SAVED!';
                await refreshBackupInfo();
                setTimeout(() => {
                    saveBtn.textContent = 'SAVE TO BACKUP';
                    saveBtn.disabled = false;
                }, 1500);
            } catch (err) {
                errorDisplay.textContent = 'Failed to save backup: ' + (err.message || 'Unknown error');
                errorDisplay.style.display = 'block';
                saveBtn.textContent = 'SAVE TO BACKUP';
                saveBtn.disabled = false;
            }
        };

        // Perform backup load
        const doLoadBackup = async () => {
            loadBtn.disabled = true;
            loadBtn.textContent = 'LOADING...';
            errorDisplay.style.display = 'none';

            try {
                const backupData = await cloudSaveManager.loadFromBackup();
                if (!backupData) {
                    throw new Error('No backup found');
                }

                console.log('[Backup] Loaded backup state:', {
                    miles: backupData.state.miles,
                    totalMiles: backupData.state.totalMiles,
                    lifetimeMiles: backupData.state.lifetimeMiles,
                    fuel: backupData.state.fuel,
                    momentum: backupData.state.session?.exitMomentum,
                    prestigeLevel: backupData.state.prestige?.level,
                    wavesNextMile: backupData.state.waves?.nextWaveAtMile,
                    starterSpeed: backupData.state.cars?.starter?.upgradeLevels?.baseSpeed
                });

                const userId = accountManager.getUserId();
                backupData.state._cloudSaveOwnerId = userId;

                // Store backup in a SEPARATE key that won't be overwritten by auto-save
                // The auth handler will read from this key and apply it
                localStorage.setItem('idlemile_pending_backup_restore', JSON.stringify(backupData.state));

                // Clear simulator state to prevent old progress
                localStorage.removeItem('offlineSimulatorState_roadTripSave_account');

                // Sync loaded state to cloud as current
                await cloudSaveManager.forceLocalToCloud(backupData.state);

                // Set flag to tell auth handler to use the pending backup
                localStorage.setItem('idlemile_backup_loaded', 'true');

                loadBtn.textContent = 'RELOADING...';
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            } catch (err) {
                errorDisplay.textContent = 'Failed to load backup: ' + (err.message || 'Unknown error');
                errorDisplay.style.display = 'block';
                loadBtn.textContent = 'LOAD FROM BACKUP';
                loadBtn.disabled = cachedBackupInfo === null;
            }
        };

        // Save button handler
        saveBtn.addEventListener('click', async () => {
            const currentState = getCurrentState();
            const currentMiles = Math.floor(currentState?.lifetimeMiles ?? currentState?.totalMiles ?? 0);

            if (cachedBackupInfo) {
                // Existing backup - confirm overwrite
                const existingMiles = Math.floor(cachedBackupInfo.lifetimeMiles).toLocaleString();
                modalManager.createConfirmation({
                    title: 'OVERWRITE BACKUP?',
                    message: `Replace existing backup (${existingMiles} mi) with current progress (${currentMiles.toLocaleString()} mi)?`,
                    confirmText: 'OVERWRITE',
                    cancelText: 'CANCEL',
                    confirmStyle: 'warning',
                    onConfirm: doSaveBackup
                });
            } else {
                // No existing backup - save directly
                doSaveBackup();
            }
        });

        // Load button handler (two-step confirmation)
        loadBtn.addEventListener('click', async () => {
            if (!cachedBackupInfo) return;

            const currentState = getCurrentState();
            const currentMiles = Math.floor(currentState?.lifetimeMiles ?? currentState?.totalMiles ?? 0);
            const backupMiles = Math.floor(cachedBackupInfo.lifetimeMiles);

            // Step 1: Confirm load
            modalManager.createConfirmation({
                title: 'LOAD BACKUP?',
                message: `Load backup from ${formatBackupDate(cachedBackupInfo.savedAt)}?\n\nYour current progress will be replaced.`,
                confirmText: 'LOAD',
                cancelText: 'CANCEL',
                onConfirm: () => {
                    // Step 2: ARE YOU SURE
                    modalManager.createConfirmation({
                        title: 'ARE YOU SURE?',
                        message: `Current: ${currentMiles.toLocaleString()} lifetime miles\nBackup: ${backupMiles.toLocaleString()} lifetime miles\n\nThis cannot be undone.`,
                        confirmText: 'YES, LOAD',
                        cancelText: 'CANCEL',
                        confirmStyle: 'danger',
                        onConfirm: doLoadBackup
                    });
                }
            });
        });

        // Initial load
        refreshBackupInfo();
    }

    // Logout button (menu context only, linked only)
    const showLogout = context === 'menu' && isLinked;
    if (showLogout) {
        const logoutSection = document.createElement('div');
        logoutSection.className = 'cf-section';

        const logoutBtn = document.createElement('button');
        logoutBtn.className = 'cf-button cf-button-full cf-button-danger';
        logoutBtn.textContent = 'SIGN OUT';
        logoutBtn.addEventListener('click', async () => {
            logoutBtn.disabled = true;
            logoutBtn.textContent = 'SIGNING OUT...';
            try {
                await accountManager.signOut();
                modal.close();
                window.location.reload();
            } catch (err) {
                logoutBtn.disabled = false;
                logoutBtn.textContent = 'SIGN OUT';
                console.error('Sign out failed:', err);
            }
        });
        logoutSection.appendChild(logoutBtn);
        content.appendChild(logoutSection);
    }

    return modal;
}

/**
 * Show Leaderboard Modal
 * @param {object} options
 * @param {LeaderboardManager} options.leaderboardManager
 * @param {ModalManager} options.modalManager
 */
export function showLeaderboardModal({ leaderboardManager, modalManager }) {
    const modal = createCFModal({ title: 'LEADERBOARDS', className: 'cf-modal-leaderboard' });
    const content = modal.body;

    // Tabs
    const tabs = createElement('div', 'cf-tabs');
    const tabData = [
        { type: 'allTime', text: 'All Time', active: true },
        { type: 'weekly', text: 'Weekly' },
        { type: 'daily', text: 'Daily' },
        { type: 'friends', text: 'Friends' }
    ];
    tabData.forEach(({ type, text, active }) => {
        const btn = createElement('button', active ? ['cf-tab', 'active'] : 'cf-tab', text);
        btn.dataset.type = type;
        tabs.appendChild(btn);
    });
    const refreshBtn = createElement('button', ['cf-tab', 'cf-refresh-btn'], '\u21BB'); // ↻
    refreshBtn.dataset.action = 'refresh';
    refreshBtn.title = 'Refresh leaderboards';
    tabs.appendChild(refreshBtn);
    content.appendChild(tabs);

    // List container
    const list = createElement('div', 'cf-list');
    content.appendChild(list);

    const renderLeaderboard = async (type) => {
        clearChildren(list);
        list.appendChild(createElement('div', 'cf-loading', 'Loading'));

        try {
            let entries;
            if (type === 'friends') {
                entries = await leaderboardManager.getFriendsLeaderboard();
            } else {
                entries = await leaderboardManager.getLeaderboard(type, 50);
            }

            clearChildren(list);

            if (entries.length === 0) {
                const emptyState = createElement('div', 'cf-empty-state');
                emptyState.appendChild(createElement('div', 'cf-empty-text', type === 'friends' ? 'No friends yet. Add some!' : 'No entries yet.'));
                list.appendChild(emptyState);
                return;
            }

            entries.forEach(entry => {
                const row = createElement('div', 'cf-leaderboard-item');
                if (entry.isCurrentUser) row.classList.add('current-user');
                if (entry.isFriend) row.classList.add('friend');

                const rankClass = entry.rank <= 3 ? ['cf-leaderboard-rank', 'top-3'] : 'cf-leaderboard-rank';
                const miles = leaderboardManager.constructor.formatMiles(entry.totalMiles);

                row.appendChild(createElement('div', rankClass, String(entry.rank)));

                const nameDiv = createElement('div', 'cf-leaderboard-name', entry.displayName);
                if (entry.prestigeLevel > 0) {
                    const prestigeSpan = createElement('span', null, `P${entry.prestigeLevel}`);
                    prestigeSpan.style.cssText = 'color: var(--cf-warning); margin-left: 6px;';
                    nameDiv.appendChild(prestigeSpan);
                }
                row.appendChild(nameDiv);

                row.appendChild(createElement('div', 'cf-leaderboard-miles', `${miles} mi`));

                list.appendChild(row);
            });
        } catch (err) {
            clearChildren(list);
            const emptyState = createElement('div', 'cf-empty-state');
            const errorText = createElement('div', 'cf-empty-text', 'Failed to load leaderboard');
            errorText.style.color = 'var(--cf-negative)';
            emptyState.appendChild(errorText);
            list.appendChild(emptyState);
        }
    };

    let currentType = 'allTime';

    tabs.addEventListener('click', (e) => {
        if (e.target.dataset.action === 'refresh') {
            // Clear all caches and refresh current leaderboard
            leaderboardManager.clearCache();
            renderLeaderboard(currentType);
            return;
        }
        if (e.target.classList.contains('cf-tab') && e.target.dataset.type) {
            tabs.querySelectorAll('.cf-tab').forEach(t => t.classList.remove('active'));
            e.target.classList.add('active');
            currentType = e.target.dataset.type;
            renderLeaderboard(currentType);
        }
    });

    renderLeaderboard('allTime');

    return modal;
}

/**
 * Show In-Game Account Modal (combines account info + friends)
 * @param {object} options
 * @param {FriendManager} options.friendManager
 * @param {AccountManager} options.accountManager
 * @param {CloudSaveManager} options.cloudSaveManager
 * @param {ModalManager} options.modalManager
 */
export function showInGameAccountModal({ friendManager, accountManager, cloudSaveManager, modalManager }) {
    const modal = createCFModal({ title: 'FRIENDS' });
    const content = modal.body;

    const isLinked = accountManager.isLinked();
    const email = accountManager.getEmail();
    const friendCode = accountManager.getFriendCode();

    // Account status (signed in as)
    if (isLinked && email) {
        const statusSection = createElement('div', 'cf-data-row');
        statusSection.appendChild(createElement('span', 'cf-data-label', 'Signed in as'));
        const emailSpan = createElement('span', 'cf-data-value', email);
        emailSpan.style.fontSize = 'var(--cf-font-size-sm)';
        statusSection.appendChild(emailSpan);
        content.appendChild(statusSection);
    }

    // Friend code display
    const codeSection = createElement('div', 'cf-info-block');
    codeSection.appendChild(createElement('div', ['cf-data-label', 'cf-mb-sm'], 'Your Friend Code'));
    const flexDiv = createElement('div', ['cf-flex', 'cf-items-center', 'cf-gap-md']);
    flexDiv.appendChild(createElement('span', 'cf-code-display', friendCode || '--------'));
    let copyBtn = null;
    if (friendCode) {
        copyBtn = createElement('button', ['cf-button', 'cf-button-small'], 'COPY');
        copyBtn.id = 'cf-copy-friend-code';
        flexDiv.appendChild(copyBtn);
    }
    codeSection.appendChild(flexDiv);
    content.appendChild(codeSection);

    if (copyBtn) {
        copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(friendCode);
            copyBtn.textContent = 'COPIED';
            setTimeout(() => copyBtn.textContent = 'COPY', 1500);
        });
    }

    // Add friend section
    const addSection = createElement('div', 'cf-section');
    addSection.appendChild(createElement('div', ['cf-data-label', 'cf-mb-sm'], 'Add Friend by Code'));
    const inputFlexDiv = createElement('div', ['cf-flex', 'cf-gap-sm']);
    const addInput = createElement('input', ['cf-input', 'cf-input-mono']);
    addInput.type = 'text';
    addInput.id = 'cf-add-code';
    addInput.placeholder = '8-CHAR CODE';
    addInput.maxLength = 8;
    addInput.style.flex = '1';
    inputFlexDiv.appendChild(addInput);
    const addBtn = createElement('button', ['cf-button', 'primary'], 'ADD');
    addBtn.id = 'cf-add-btn';
    inputFlexDiv.appendChild(addBtn);
    addSection.appendChild(inputFlexDiv);
    const addError = createElement('div', 'cf-error-text');
    addError.id = 'cf-add-error';
    addError.style.display = 'none';
    addSection.appendChild(addError);
    const addSuccess = createElement('div', 'cf-success-text');
    addSuccess.id = 'cf-add-success';
    addSuccess.style.display = 'none';
    addSection.appendChild(addSuccess);
    content.appendChild(addSection);

    addBtn.addEventListener('click', async () => {
        const code = addInput.value.trim().toUpperCase();
        addError.style.display = 'none';
        addSuccess.style.display = 'none';

        if (code.length !== 8) {
            addError.textContent = 'Friend code must be 8 characters';
            addError.style.display = 'block';
            return;
        }

        addBtn.disabled = true;
        addBtn.textContent = '...';

        try {
            const result = await friendManager.sendFriendRequest(code);
            if (result.autoAccepted) {
                addSuccess.textContent = 'You are now friends!';
            } else {
                addSuccess.textContent = 'Friend request sent!';
            }
            addSuccess.style.display = 'block';
            addInput.value = '';
            refreshLists();
        } catch (err) {
            addError.textContent = err.message || 'Failed to send request';
            addError.style.display = 'block';
        } finally {
            addBtn.disabled = false;
            addBtn.textContent = 'ADD';
        }
    });

    // Tabs
    const tabs = createElement('div', ['cf-tabs', 'cf-mt-md']);
    const friendsTab = createElement('button', ['cf-tab', 'active'], 'Friends');
    friendsTab.dataset.section = 'friends';
    tabs.appendChild(friendsTab);
    const requestsTab = createElement('button', 'cf-tab', 'Requests ');
    requestsTab.dataset.section = 'requests';
    const requestCountSpan = createElement('span', 'cf-tab-badge');
    requestCountSpan.id = 'cf-request-count';
    requestsTab.appendChild(requestCountSpan);
    tabs.appendChild(requestsTab);
    content.appendChild(tabs);

    // List container
    const listContainer = createElement('div', 'cf-list');
    listContainer.style.maxHeight = '200px';
    listContainer.style.overflowY = 'auto';
    content.appendChild(listContainer);

    let currentSection = 'friends';

    const renderFriendsList = async () => {
        clearChildren(listContainer);
        listContainer.appendChild(createElement('div', 'cf-loading', 'Loading'));

        try {
            const friends = await friendManager.getFriends();
            clearChildren(listContainer);

            if (friends.length === 0) {
                const emptyState = createElement('div', 'cf-empty-state');
                emptyState.appendChild(createElement('div', 'cf-empty-text', 'No friends yet'));
                listContainer.appendChild(emptyState);
                return;
            }

            friends.forEach(friend => {
                const row = createElement('div', 'cf-list-item');

                const contentDiv = createElement('div', 'cf-list-item-content');
                contentDiv.appendChild(createElement('div', 'cf-list-item-primary', friend.displayName));
                contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', friend.friendCode));
                row.appendChild(contentDiv);

                const actionsDiv = createElement('div', 'cf-list-item-actions');
                const removeBtn = createElement('button', ['cf-button', 'cf-button-small', 'cf-button-danger'], 'REMOVE');
                removeBtn.dataset.remove = friend.oderId;
                actionsDiv.appendChild(removeBtn);
                row.appendChild(actionsDiv);

                removeBtn.addEventListener('click', async () => {
                    removeBtn.disabled = true;
                    removeBtn.textContent = '...';
                    try {
                        await friendManager.removeFriend(friend.oderId);
                        row.remove();
                    } catch (err) {
                        removeBtn.disabled = false;
                        removeBtn.textContent = 'REMOVE';
                    }
                });

                listContainer.appendChild(row);
            });
        } catch (err) {
            clearChildren(listContainer);
            const emptyState = createElement('div', 'cf-empty-state');
            const errorText = createElement('div', 'cf-empty-text', 'Failed to load friends');
            errorText.style.color = 'var(--cf-negative)';
            emptyState.appendChild(errorText);
            listContainer.appendChild(emptyState);
        }
    };

    const renderRequestsList = async () => {
        clearChildren(listContainer);
        listContainer.appendChild(createElement('div', 'cf-loading', 'Loading'));

        try {
            const pending = await friendManager.getPendingRequests();
            const sent = await friendManager.getSentRequests();

            clearChildren(listContainer);

            // Update count badge
            const countBadge = tabs.querySelector('#cf-request-count');
            countBadge.textContent = pending.length > 0 ? `(${pending.length})` : '';

            if (pending.length === 0 && sent.length === 0) {
                const emptyState = createElement('div', 'cf-empty-state');
                emptyState.appendChild(createElement('div', 'cf-empty-text', 'No pending requests'));
                listContainer.appendChild(emptyState);
                return;
            }

            // Incoming requests
            if (pending.length > 0) {
                const header = createElement('div', 'cf-title', 'INCOMING');
                listContainer.appendChild(header);

                pending.forEach(req => {
                    const row = createElement('div', 'cf-list-item');

                    const contentDiv = createElement('div', 'cf-list-item-content');
                    contentDiv.appendChild(createElement('div', 'cf-list-item-primary', req.fromDisplayName));
                    contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', req.fromFriendCode));
                    row.appendChild(contentDiv);

                    const actionsDiv = createElement('div', 'cf-list-item-actions');
                    const acceptBtn = createElement('button', ['cf-button', 'cf-button-small', 'cf-button-positive'], 'ACCEPT');
                    acceptBtn.dataset.accept = req.id;
                    const declineBtn = createElement('button', ['cf-button', 'cf-button-small'], 'DECLINE');
                    declineBtn.dataset.decline = req.id;
                    actionsDiv.appendChild(acceptBtn);
                    actionsDiv.appendChild(declineBtn);
                    row.appendChild(actionsDiv);

                    acceptBtn.addEventListener('click', async () => {
                        acceptBtn.disabled = true;
                        try {
                            await friendManager.acceptFriendRequest(req.id);
                            row.remove();
                            refreshLists();
                        } catch (err) {
                            acceptBtn.disabled = false;
                        }
                    });

                    declineBtn.addEventListener('click', async () => {
                        declineBtn.disabled = true;
                        try {
                            await friendManager.declineFriendRequest(req.id);
                            row.remove();
                            // Update count badge
                            const currentCount = parseInt(countBadge.textContent.replace(/[()]/g, '')) || 0;
                            const newCount = Math.max(0, currentCount - 1);
                            countBadge.textContent = newCount > 0 ? `(${newCount})` : '';
                        } catch (err) {
                            declineBtn.disabled = false;
                        }
                    });

                    listContainer.appendChild(row);
                });
            }

            // Sent requests
            if (sent.length > 0) {
                const header = createElement('div', 'cf-title', 'SENT');
                listContainer.appendChild(header);

                sent.forEach(req => {
                    const row = createElement('div', 'cf-list-item');
                    row.style.opacity = '0.6';

                    const contentDiv = createElement('div', 'cf-list-item-content');
                    contentDiv.appendChild(createElement('div', 'cf-list-item-primary', `To: ${req.toDisplayName || 'Unknown'}`));
                    contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', 'Pending...'));
                    row.appendChild(contentDiv);

                    const actionsDiv = createElement('div', 'cf-list-item-actions');
                    const cancelBtn = createElement('button', ['cf-button', 'cf-button-small'], 'CANCEL');
                    cancelBtn.dataset.cancel = req.id;
                    actionsDiv.appendChild(cancelBtn);
                    row.appendChild(actionsDiv);

                    cancelBtn.addEventListener('click', async () => {
                        cancelBtn.disabled = true;
                        try {
                            await friendManager.cancelFriendRequest(req.id);
                            row.remove();
                        } catch (err) {
                            cancelBtn.disabled = false;
                        }
                    });

                    listContainer.appendChild(row);
                });
            }
        } catch (err) {
            clearChildren(listContainer);
            const emptyState = createElement('div', 'cf-empty-state');
            const errorText = createElement('div', 'cf-empty-text', 'Failed to load requests');
            errorText.style.color = 'var(--cf-negative)';
            emptyState.appendChild(errorText);
            listContainer.appendChild(emptyState);
        }
    };

    const refreshLists = () => {
        if (currentSection === 'friends') {
            renderFriendsList();
        } else {
            renderRequestsList();
        }
    };

    tabs.addEventListener('click', (e) => {
        if (e.target.classList.contains('cf-tab')) {
            tabs.querySelectorAll('.cf-tab').forEach(t => t.classList.remove('active'));
            e.target.classList.add('active');
            currentSection = e.target.dataset.section;
            refreshLists();
        }
    });

    renderFriendsList();

    // Load request count
    friendManager.getPendingRequests().then(pending => {
        const countBadge = tabs.querySelector('#cf-request-count');
        if (pending.length > 0) {
            countBadge.textContent = `(${pending.length})`;
        }
    });

    // Sync status section at the bottom
    if (cloudSaveManager && isLinked) {
        const syncSection = document.createElement('div');
        syncSection.className = 'cf-section';

        const updateSyncStatus = (status) => {
            const lastSync = cloudSaveManager.getLastSyncTime();
            const lastSyncText = lastSync ? new Date(lastSync).toLocaleTimeString() : 'Never';

            let dotClass = '';
            let statusText = 'Synced';

            if (status?.status === 'syncing') {
                dotClass = 'syncing';
                statusText = 'Syncing...';
            } else if (status?.status === 'error') {
                dotClass = 'error';
                statusText = 'Sync error';
            }

            clearChildren(syncSection);
            const syncStatusDiv = createElement('div', 'cf-sync-status');
            const dotSpan = createElement('span', dotClass ? ['cf-sync-dot', dotClass] : 'cf-sync-dot');
            const statusSpan = createElement('span', null, statusText);
            const lastSpan = createElement('span', null, `Last: ${lastSyncText}`);
            lastSpan.style.marginLeft = 'auto';
            syncStatusDiv.appendChild(dotSpan);
            syncStatusDiv.appendChild(statusSpan);
            syncStatusDiv.appendChild(lastSpan);
            syncSection.appendChild(syncStatusDiv);
        };

        updateSyncStatus();
        const unsubscribe = cloudSaveManager.onSyncStatusChanged(updateSyncStatus);
        modal.overlay.addEventListener('remove', unsubscribe);

        content.appendChild(syncSection);
    }

    return modal;
}

/**
 * Show Friends Modal (for main menu)
 * @param {object} options
 * @param {FriendManager} options.friendManager
 * @param {AccountManager} options.accountManager
 * @param {ModalManager} options.modalManager
 */
export function showFriendsModal({ friendManager, accountManager, modalManager }) {
    const modal = createCFModal({ title: 'FRIENDS' });
    const content = modal.body;

    const friendCode = accountManager.getFriendCode();

    // Friend code display
    const codeSection = createElement('div', 'cf-info-block');
    codeSection.appendChild(createElement('div', ['cf-data-label', 'cf-mb-sm'], 'Your Friend Code'));
    const flexDiv = createElement('div', ['cf-flex', 'cf-items-center', 'cf-gap-md']);
    flexDiv.appendChild(createElement('span', 'cf-code-display', friendCode || '--------'));
    let copyBtn = null;
    if (friendCode) {
        copyBtn = createElement('button', ['cf-button', 'cf-button-small'], 'COPY');
        copyBtn.id = 'cf-copy-friend-code';
        flexDiv.appendChild(copyBtn);
    }
    codeSection.appendChild(flexDiv);
    content.appendChild(codeSection);

    if (copyBtn) {
        copyBtn.addEventListener('click', () => {
            navigator.clipboard.writeText(friendCode);
            copyBtn.textContent = 'COPIED';
            setTimeout(() => copyBtn.textContent = 'COPY', 1500);
        });
    }

    // Add friend section
    const addSection = createElement('div', 'cf-section');
    addSection.appendChild(createElement('div', ['cf-data-label', 'cf-mb-sm'], 'Add Friend by Code'));
    const inputFlexDiv = createElement('div', ['cf-flex', 'cf-gap-sm']);
    const addInput = createElement('input', ['cf-input', 'cf-input-mono']);
    addInput.type = 'text';
    addInput.id = 'cf-add-code';
    addInput.placeholder = '8-CHAR CODE';
    addInput.maxLength = 8;
    addInput.style.flex = '1';
    inputFlexDiv.appendChild(addInput);
    const addBtn = createElement('button', ['cf-button', 'primary'], 'ADD');
    addBtn.id = 'cf-add-btn';
    inputFlexDiv.appendChild(addBtn);
    addSection.appendChild(inputFlexDiv);
    const addError = createElement('div', 'cf-error-text');
    addError.id = 'cf-add-error';
    addError.style.display = 'none';
    addSection.appendChild(addError);
    const addSuccess = createElement('div', 'cf-success-text');
    addSuccess.id = 'cf-add-success';
    addSuccess.style.display = 'none';
    addSection.appendChild(addSuccess);
    content.appendChild(addSection);

    addBtn.addEventListener('click', async () => {
        const code = addInput.value.trim().toUpperCase();
        addError.style.display = 'none';
        addSuccess.style.display = 'none';

        if (code.length !== 8) {
            addError.textContent = 'Friend code must be 8 characters';
            addError.style.display = 'block';
            return;
        }

        addBtn.disabled = true;
        addBtn.textContent = '...';

        try {
            const result = await friendManager.sendFriendRequest(code);
            if (result.autoAccepted) {
                addSuccess.textContent = 'You are now friends!';
            } else {
                addSuccess.textContent = 'Friend request sent!';
            }
            addSuccess.style.display = 'block';
            addInput.value = '';
            refreshLists();
        } catch (err) {
            addError.textContent = err.message || 'Failed to send request';
            addError.style.display = 'block';
        } finally {
            addBtn.disabled = false;
            addBtn.textContent = 'ADD';
        }
    });

    // Tabs
    const tabs = createElement('div', ['cf-tabs', 'cf-mt-md']);
    const friendsTab = createElement('button', ['cf-tab', 'active'], 'Friends');
    friendsTab.dataset.section = 'friends';
    tabs.appendChild(friendsTab);
    const requestsTab = createElement('button', 'cf-tab', 'Requests ');
    requestsTab.dataset.section = 'requests';
    const requestCountSpan = createElement('span', 'cf-tab-badge');
    requestCountSpan.id = 'cf-request-count';
    requestsTab.appendChild(requestCountSpan);
    tabs.appendChild(requestsTab);
    content.appendChild(tabs);

    // List container
    const listContainer = createElement('div', 'cf-list');
    listContainer.style.maxHeight = '280px';
    listContainer.style.overflowY = 'auto';
    content.appendChild(listContainer);

    let currentSection = 'friends';

    const renderFriendsList = async () => {
        clearChildren(listContainer);
        listContainer.appendChild(createElement('div', 'cf-loading', 'Loading'));

        try {
            const friends = await friendManager.getFriends();
            clearChildren(listContainer);

            if (friends.length === 0) {
                const emptyState = createElement('div', 'cf-empty-state');
                emptyState.appendChild(createElement('div', 'cf-empty-text', 'No friends yet'));
                listContainer.appendChild(emptyState);
                return;
            }

            friends.forEach(friend => {
                const row = createElement('div', 'cf-list-item');

                const contentDiv = createElement('div', 'cf-list-item-content');
                contentDiv.appendChild(createElement('div', 'cf-list-item-primary', friend.displayName));
                contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', friend.friendCode));
                row.appendChild(contentDiv);

                const actionsDiv = createElement('div', 'cf-list-item-actions');
                const removeBtn = createElement('button', ['cf-button', 'cf-button-small', 'cf-button-danger'], 'REMOVE');
                removeBtn.dataset.remove = friend.oderId;
                actionsDiv.appendChild(removeBtn);
                row.appendChild(actionsDiv);

                removeBtn.addEventListener('click', async () => {
                    removeBtn.disabled = true;
                    removeBtn.textContent = '...';
                    try {
                        await friendManager.removeFriend(friend.oderId);
                        row.remove();
                    } catch (err) {
                        removeBtn.disabled = false;
                        removeBtn.textContent = 'REMOVE';
                    }
                });

                listContainer.appendChild(row);
            });
        } catch (err) {
            clearChildren(listContainer);
            const emptyState = createElement('div', 'cf-empty-state');
            const errorText = createElement('div', 'cf-empty-text', 'Failed to load friends');
            errorText.style.color = 'var(--cf-negative)';
            emptyState.appendChild(errorText);
            listContainer.appendChild(emptyState);
        }
    };

    const renderRequestsList = async () => {
        clearChildren(listContainer);
        listContainer.appendChild(createElement('div', 'cf-loading', 'Loading'));

        try {
            const pending = await friendManager.getPendingRequests();
            const sent = await friendManager.getSentRequests();

            clearChildren(listContainer);

            // Update count badge
            const countBadge = tabs.querySelector('#cf-request-count');
            countBadge.textContent = pending.length > 0 ? `(${pending.length})` : '';

            if (pending.length === 0 && sent.length === 0) {
                const emptyState = createElement('div', 'cf-empty-state');
                emptyState.appendChild(createElement('div', 'cf-empty-text', 'No pending requests'));
                listContainer.appendChild(emptyState);
                return;
            }

            // Incoming requests
            if (pending.length > 0) {
                const header = createElement('div', 'cf-title', 'INCOMING');
                listContainer.appendChild(header);

                pending.forEach(req => {
                    const row = createElement('div', 'cf-list-item');

                    const contentDiv = createElement('div', 'cf-list-item-content');
                    contentDiv.appendChild(createElement('div', 'cf-list-item-primary', req.fromDisplayName));
                    contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', req.fromFriendCode));
                    row.appendChild(contentDiv);

                    const actionsDiv = createElement('div', 'cf-list-item-actions');
                    const acceptBtn = createElement('button', ['cf-button', 'cf-button-small', 'cf-button-positive'], 'ACCEPT');
                    acceptBtn.dataset.accept = req.id;
                    const declineBtn = createElement('button', ['cf-button', 'cf-button-small'], 'DECLINE');
                    declineBtn.dataset.decline = req.id;
                    actionsDiv.appendChild(acceptBtn);
                    actionsDiv.appendChild(declineBtn);
                    row.appendChild(actionsDiv);

                    acceptBtn.addEventListener('click', async () => {
                        acceptBtn.disabled = true;
                        try {
                            await friendManager.acceptFriendRequest(req.id);
                            row.remove();
                            refreshLists();
                        } catch (err) {
                            acceptBtn.disabled = false;
                        }
                    });

                    declineBtn.addEventListener('click', async () => {
                        declineBtn.disabled = true;
                        try {
                            await friendManager.declineFriendRequest(req.id);
                            row.remove();
                            // Update count badge
                            const currentCount = parseInt(countBadge.textContent.replace(/[()]/g, '')) || 0;
                            const newCount = Math.max(0, currentCount - 1);
                            countBadge.textContent = newCount > 0 ? `(${newCount})` : '';
                        } catch (err) {
                            declineBtn.disabled = false;
                        }
                    });

                    listContainer.appendChild(row);
                });
            }

            // Sent requests
            if (sent.length > 0) {
                const header = createElement('div', 'cf-title', 'SENT');
                listContainer.appendChild(header);

                sent.forEach(req => {
                    const row = createElement('div', 'cf-list-item');
                    row.style.opacity = '0.6';

                    const contentDiv = createElement('div', 'cf-list-item-content');
                    contentDiv.appendChild(createElement('div', 'cf-list-item-primary', `To: ${req.toDisplayName || 'Unknown'}`));
                    contentDiv.appendChild(createElement('div', 'cf-list-item-secondary', 'Pending...'));
                    row.appendChild(contentDiv);

                    const actionsDiv = createElement('div', 'cf-list-item-actions');
                    const cancelBtn = createElement('button', ['cf-button', 'cf-button-small'], 'CANCEL');
                    cancelBtn.dataset.cancel = req.id;
                    actionsDiv.appendChild(cancelBtn);
                    row.appendChild(actionsDiv);

                    cancelBtn.addEventListener('click', async () => {
                        cancelBtn.disabled = true;
                        try {
                            await friendManager.cancelFriendRequest(req.id);
                            row.remove();
                        } catch (err) {
                            cancelBtn.disabled = false;
                        }
                    });

                    listContainer.appendChild(row);
                });
            }
        } catch (err) {
            clearChildren(listContainer);
            const emptyState = createElement('div', 'cf-empty-state');
            const errorText = createElement('div', 'cf-empty-text', 'Failed to load requests');
            errorText.style.color = 'var(--cf-negative)';
            emptyState.appendChild(errorText);
            listContainer.appendChild(emptyState);
        }
    };

    const refreshLists = () => {
        if (currentSection === 'friends') {
            renderFriendsList();
        } else {
            renderRequestsList();
        }
    };

    tabs.addEventListener('click', (e) => {
        if (e.target.classList.contains('cf-tab')) {
            tabs.querySelectorAll('.cf-tab').forEach(t => t.classList.remove('active'));
            e.target.classList.add('active');
            currentSection = e.target.dataset.section;
            refreshLists();
        }
    });

    renderFriendsList();

    // Load request count
    friendManager.getPendingRequests().then(pending => {
        const countBadge = tabs.querySelector('#cf-request-count');
        if (pending.length > 0) {
            countBadge.textContent = `(${pending.length})`;
        }
    });

    return modal;
}

/**
 * Helper: format time ago
 */
function formatTimeAgo(date) {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 120) return '1 minute ago';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 7200) return '1 hour ago';
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    return `${Math.floor(seconds / 86400)} days ago`;
}

/**
 * Show save conflict modal - lets user choose between local and cloud saves
 * Returns a Promise that resolves to 'local' or 'cloud'
 * @param {object} options
 * @param {object} options.localState - Local save state
 * @param {object} options.cloudState - Cloud save state
 * @param {number} options.cloudTimestamp - Cloud save timestamp (ms)
 * @returns {Promise<'local'|'cloud'>}
 */
export function showSaveConflictChoice({ localState, cloudState, cloudTimestamp }) {
    return new Promise((resolve) => {
        const formatTime = (timestamp) => {
            if (!timestamp) return 'Unknown';
            const now = Date.now();
            const diff = now - timestamp;
            const mins = Math.floor(diff / 60000);
            const hours = Math.floor(diff / 3600000);
            const days = Math.floor(diff / 86400000);

            if (mins < 1) return 'Just now';
            if (mins < 60) return `${mins}m ago`;
            if (hours < 24) return `${hours}h ago`;
            if (days < 7) return `${days}d ago`;
            return new Date(timestamp).toLocaleDateString();
        };

        const localMiles = Math.floor(localState?.lifetimeMiles ?? localState?.totalMiles ?? 0);
        const cloudMiles = Math.floor(cloudState?.lifetimeMiles ?? cloudState?.totalMiles ?? 0);
        const localTime = localState?._lastSaveTime || Date.now();
        const cloudTime = cloudTimestamp || 0;

        const modal = createCFModal({
            title: 'SAVE CONFLICT',
            onClose: () => resolve('cancelled') // User X'd out - cancel login
        });

        // Build modal body with DOM APIs
        const questionP = createElement('p', null, 'Which save would you like to keep?');
        questionP.style.cssText = 'text-align: center; color: var(--cf-text-secondary); margin-bottom: var(--cf-space-lg);';
        modal.body.appendChild(questionP);

        const optionsDiv = createElement('div', 'cf-conflict-options');

        // Local option button
        const localBtn = createElement('button', 'cf-conflict-option');
        localBtn.dataset.choice = 'local';
        localBtn.appendChild(createElement('div', 'cf-conflict-label', 'THIS DEVICE'));
        const localStats = createElement('div', 'cf-conflict-stats');
        const localMilesDiv = createElement('div');
        const localMilesStrong = createElement('strong', null, localMiles.toLocaleString());
        localMilesDiv.appendChild(localMilesStrong);
        localMilesDiv.appendChild(document.createTextNode(' lifetime miles'));
        localStats.appendChild(localMilesDiv);
        const localTimeDiv = createElement('div', null, formatTime(localTime));
        localTimeDiv.style.opacity = '0.6';
        localStats.appendChild(localTimeDiv);
        localBtn.appendChild(localStats);
        optionsDiv.appendChild(localBtn);

        // Cloud option button
        const cloudBtn = createElement('button', 'cf-conflict-option');
        cloudBtn.dataset.choice = 'cloud';
        cloudBtn.appendChild(createElement('div', 'cf-conflict-label', 'CLOUD'));
        const cloudStats = createElement('div', 'cf-conflict-stats');
        const cloudMilesDiv = createElement('div');
        const cloudMilesStrong = createElement('strong', null, cloudMiles.toLocaleString());
        cloudMilesDiv.appendChild(cloudMilesStrong);
        cloudMilesDiv.appendChild(document.createTextNode(' lifetime miles'));
        cloudStats.appendChild(cloudMilesDiv);
        const cloudTimeDiv = createElement('div', null, formatTime(cloudTime));
        cloudTimeDiv.style.opacity = '0.6';
        cloudStats.appendChild(cloudTimeDiv);
        cloudBtn.appendChild(cloudStats);
        optionsDiv.appendChild(cloudBtn);

        modal.body.appendChild(optionsDiv);

        const warningP = createElement('p', null, 'The other save will be overwritten');
        warningP.style.cssText = 'text-align: center; color: var(--cf-warning); font-size: var(--cf-font-size-xs); margin-top: var(--cf-space-md);';
        modal.body.appendChild(warningP);

        // Add styles for conflict options
        const style = document.createElement('style');
        style.textContent = `
            .cf-conflict-options {
                display: flex;
                flex-direction: column;
                gap: var(--cf-space-md);
            }
            .cf-conflict-option {
                background: var(--cf-bg-secondary);
                border: 1px solid var(--cf-divider);
                padding: var(--cf-space-md);
                cursor: pointer;
                text-align: left;
                font-family: var(--cf-font-mono);
                transition: all 0.15s ease;
            }
            .cf-conflict-option:hover {
                border-color: var(--cf-accent);
                background: rgba(255,255,255,0.05);
            }
            .cf-conflict-label {
                font-size: var(--cf-font-size-sm);
                font-weight: 600;
                letter-spacing: 1px;
                margin-bottom: var(--cf-space-sm);
                color: var(--cf-text-primary);
            }
            .cf-conflict-stats {
                font-size: var(--cf-font-size-sm);
                color: var(--cf-text-secondary);
            }
            .cf-conflict-stats strong {
                color: var(--cf-text-primary);
            }
        `;
        modal.modal.appendChild(style);

        // Handle clicks
        modal.body.querySelectorAll('.cf-conflict-option').forEach(btn => {
            btn.addEventListener('click', () => {
                const choice = btn.dataset.choice;
                resolve(choice);  // Resolve BEFORE close (close triggers onClose which would resolve 'cancelled')
                modal.close();
            });
        });
    });
}
